"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@punchup/ui-common/shadcn/ui/accordion";

import { ReactNode } from "react";

export interface CollapsibleSettingsSectionProps {
  /** Unique identifier for this section */
  id: string;
  /** Section title shown in accordion header */
  title: string;
  /** Section description shown when expanded */
  description?: string | ReactNode;
  /** Section content */
  children: ReactNode;
  /** Number of settings fields in this section */
  count?: number;
  /**
   * When true, renders a small orange dot next to the section title to signal
   * unsaved changes — the same VSCode/GitHub tab-dot convention used in the
   * tixologi PunchupAccordionSection (PunchupAccordion.module.css → .unsavedDot).
   * Mirror: keep visual spec in sync with tixologi. See show/CLAUDE.md.
   */
  isDirty?: boolean;
}

/**
 * Individual accordion item for a settings section.
 * Must be used inside a CollapsibleSettingsSectionGroup.
 */
export function CollapsibleSettingsSection({
  id,
  title,
  description,
  children,
  count,
  isDirty = false,
}: CollapsibleSettingsSectionProps) {
  return (
    <AccordionItem
      value={id}
      className="border border-neutral-200 rounded-lg overflow-hidden"
    >
      <AccordionTrigger className="hover:no-underline py-4 px-4 md:py-5 md:px-6 items-center hover:bg-[var(--punchup-color-surface)] transition-colors">
        <div className="flex flex-row gap-x-4 items-center justify-between w-full">
          <div className="flex flex-1 flex-col items-start gap-1">
            <span className="flex items-center gap-2 text-xl font-semibold text-primary-blue">
              {title}
              {isDirty && (
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: "var(--punchup-color-primary)" }}
                  title="Unsaved changes"
                  aria-label="Unsaved changes"
                />
              )}
            </span>
            {description && (
              /* text-sm font-normal text-secondary-blue font-inter:
                 Matches .descriptionText in ShowComponent.module.css and AccordionDescription
                 in tixologi's PunchupAccordion.styles.ts — keep all three in sync. */
              <p className="text-sm text-secondary-blue font-normal font-inter text-left">
                {description}
              </p>
            )}
          </div>
          {count !== undefined && (
            <div className="flex flex-col justify-center items-center w-6 h-6 bg-neutral-100 rounded-full">
              <span className="text-sm text-secondary-blue font-medium">
                {count}
              </span>
            </div>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="p-6 border-t border-neutral-200">
        <div className="flex flex-col gap-6">{children}</div>
      </AccordionContent>
    </AccordionItem>
  );
}

export interface CollapsibleSettingsSectionGroupProps {
  /** Section IDs that should be open by default (uncontrolled mode) */
  defaultOpen?: string[];
  /** Currently open section IDs (controlled mode) */
  value?: string[];
  /** Callback when open sections change (controlled mode) */
  onValueChange?: (value: string[]) => void;
  /** Children should be CollapsibleSettingsSection components */
  children: ReactNode;
}

/**
 * Wrapper component that provides the accordion context.
 * All CollapsibleSettingsSection components must be children of this component.
 * Can be used in controlled mode (with value/onValueChange) or uncontrolled mode (with defaultOpen).
 */
export function CollapsibleSettingsSectionGroup({
  defaultOpen = [],
  value,
  onValueChange,
  children,
}: CollapsibleSettingsSectionGroupProps) {
  // Use controlled mode if value is provided
  const isControlled = value !== undefined;

  return (
    <Accordion
      type="multiple"
      defaultValue={isControlled ? undefined : defaultOpen}
      value={isControlled ? value : undefined}
      onValueChange={isControlled ? onValueChange : undefined}
      className="flex flex-col gap-4 w-full"
    >
      {children}
    </Accordion>
  );
}

export default CollapsibleSettingsSection;
